// Boat and Boat Roster Types

export type BoatClass = '1x' | '2-' | '2x' | '2+' | '4-' | '4x' | '4+' | '8+';
export type RowingSide = 'port' | 'starboard' | 'both' | 'na';

/**
 * Peach export “Boat Info” tab maps to boats + boat_class (GLY-264).
 * Parser: `api/utils/parsePeachFileComprehensive.js` → `boatInfo`.
 *
 * | Peach column   | App field                                      |
 * |----------------|------------------------------------------------|
 * | Boat Name      | `name`                                         |
 * | Seats          | `seat_count` (parsed); `boat_class` from seats+cox+rig |
 * | Coxed          | `coxed`                                        |
 * | Rig            | `rig` (Boat Info column only; not Rig Info)   |
 * | Manufacturer   | `manufacturer`                                 |
 * | Description    | `description` (and optionally `model`)         |
 */
export interface Boat {
  id: string;
  team_id: string;
  name: string;
  boat_class: BoatClass;
  is_primary: boolean;
  description?: string | null;
  /** Peach / equipment metadata (nullable for existing rows). */
  manufacturer?: string | null;
  /** Seeded builder lookup; optional. */
  manufacturer_id?: string | null;
  model?: string | null;
  /**
   * Peach **Boat Info** tab, Rig column (e.g. sweep/scull wording from export).
   * Distinct from per-seat **Rig Info** — see `boat_seat_rigging`.
   */
  rig?: string | null;
  /** Coarse sweep vs scull; optional. */
  rig_type_id?: string | null;
  /** Rowing seats only (1–8), not including cox. */
  seat_count?: number | null;
  coxed?: boolean | null;
  created_at: string;
  updated_at: string;
}

/** Peach Rig Info: oar side per rowing seat (port / starboard). */
export type OarRigSide = 'port' | 'starboard';

export interface BoatSeatRiggingRow {
  id: string;
  boat_id: string;
  seat_index: number;
  oar_side: OarRigSide;
  created_at: string;
  updated_at: string;
}

export interface BoatRosterEntry {
  id: string;
  boat_id: string;
  rower_id: string;
  position: number; // 1-8 for rowers, 9 for coxswain
  side: RowingSide | null;
  notes?: string | null;
  assigned_at: string;
  assigned_by?: string | null;
}

// Extended boat with roster entries
export interface BoatWithRoster extends Boat {
  roster: BoatRosterEntry[];
}

// Boat roster entry with rower profile details
export interface BoatRosterEntryWithRower extends BoatRosterEntry {
  rower: {
    id: string;
    first_name: string;
    last_name: string;
    preferred_name?: string | null;
  };
}

// Create boat payload
export interface CreateBoatPayload {
  team_id: string;
  name: string;
  boat_class: BoatClass;
  is_primary?: boolean;
  description?: string;
  manufacturer?: string | null;
  manufacturer_id?: string | null;
  model?: string | null;
  rig?: string | null;
  rig_type_id?: string | null;
  seat_count?: number | null;
  coxed?: boolean | null;
}

// Update boat payload
export interface UpdateBoatPayload {
  name?: string;
  boat_class?: BoatClass;
  is_primary?: boolean;
  description?: string | null;
  manufacturer?: string | null;
  manufacturer_id?: string | null;
  model?: string | null;
  rig?: string | null;
  rig_type_id?: string | null;
  seat_count?: number | null;
  coxed?: boolean | null;
}

// Assign rower to boat seat payload
export interface AssignBoatSeatPayload {
  boat_id: string;
  rower_id: string;
  position: number;
  side?: RowingSide;
  notes?: string;
}

// Boat class metadata
export interface BoatClassInfo {
  class: BoatClass;
  label: string;
  seats: number; // Number of rower seats (not including cox)
  hasCox: boolean;
  description: string;
}

export const BOAT_CLASSES: Record<BoatClass, BoatClassInfo> = {
  '1x': {
    class: '1x',
    label: 'Single Scull (1x)',
    seats: 1,
    hasCox: false,
    description: 'One sculler with two oars',
  },
  '2-': {
    class: '2-',
    label: 'Pair (2-)',
    seats: 2,
    hasCox: false,
    description: 'Two sweep rowers, no coxswain',
  },
  '2x': {
    class: '2x',
    label: 'Double Scull (2x)',
    seats: 2,
    hasCox: false,
    description: 'Two scullers with two oars each',
  },
  '2+': {
    class: '2+',
    label: 'Coxed Pair (2+)',
    seats: 2,
    hasCox: true,
    description: 'Two sweep rowers with coxswain',
  },
  '4-': {
    class: '4-',
    label: 'Four (4-)',
    seats: 4,
    hasCox: false,
    description: 'Four sweep rowers, no coxswain',
  },
  '4x': {
    class: '4x',
    label: 'Quad Scull (4x)',
    seats: 4,
    hasCox: false,
    description: 'Four scullers with two oars each',
  },
  '4+': {
    class: '4+',
    label: 'Coxed Four (4+)',
    seats: 4,
    hasCox: true,
    description: 'Four sweep rowers with coxswain',
  },
  '8+': {
    class: '8+',
    label: 'Eight (8+)',
    seats: 8,
    hasCox: true,
    description: 'Eight sweep rowers with coxswain',
  },
};

// Helper to get total positions (seats + cox if applicable)
export function getTotalPositions(boatClass: BoatClass): number {
  const info = BOAT_CLASSES[boatClass];
  return info.seats + (info.hasCox ? 1 : 0);
}

// Helper to check if position is coxswain
export function isCoxPosition(boatClass: BoatClass, position: number): boolean {
  const info = BOAT_CLASSES[boatClass];
  return info.hasCox && position === info.seats + 1;
}

// Helper to get position label
export function getPositionLabel(boatClass: BoatClass, position: number): string {
  const info = BOAT_CLASSES[boatClass];
  if (info.hasCox && position === info.seats + 1) {
    return 'Cox';
  }
  return `Seat ${position}`;
}

/** One-line summary of optional equipment fields for list cards. */
export function formatBoatEquipmentSummary(
  boat: Pick<Boat, 'manufacturer' | 'model' | 'rig' | 'seat_count' | 'coxed'>
): string | null {
  const parts: string[] = [];
  if (boat.manufacturer?.trim()) parts.push(boat.manufacturer.trim());
  if (boat.model?.trim()) parts.push(boat.model.trim());
  if (boat.rig?.trim()) parts.push(boat.rig.trim());
  if (boat.seat_count != null && boat.seat_count >= 1) {
    parts.push(`${boat.seat_count} seat${boat.seat_count === 1 ? '' : 's'}`);
  }
  if (boat.coxed === true) parts.push('coxed');
  if (boat.coxed === false) parts.push('no cox');
  return parts.length > 0 ? parts.join(' · ') : null;
}

/** Typical sweep eight alternating pattern (Peach: seat 1 Stbd, 2 Port, …). */
export function alternatingSweepRigSides(seatCount: number): OarRigSide[] {
  const n = Math.min(8, Math.max(0, Math.floor(seatCount)));
  const out: OarRigSide[] = [];
  for (let i = 1; i <= n; i += 1) {
    out.push(i % 2 === 1 ? 'starboard' : 'port');
  }
  return out;
}
